/**
 * ConfigTabContent — Config tab content for the UnifiedBottomPanel.
 *
 * Renders three rows:
 * 1. Reference row: thumbnails + [+] button → opens ReferenceSelector
 * 2. Prompt textarea: full-width, pre-populated, real-time sync
 * 3. Control bar: config pill, style button, retry pill+popover, sparks count, generate button
 *
 * Config flyout popovers per node type:
 * - ImageGenerator: quality (512P/1K/2K/4K) + aspect ratio grid
 * - LocationDesigner: time of day + weather
 * - Character/Object: no config pill, no flyout
 */

import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useReactFlow } from 'reactflow'
import { getThumbnailUrl } from '../../../../api/useAssetUrl'
import { ReferenceSelector, type SelectedReference } from './ReferenceSelector'
import { RichTextBox, type RichTextBoxState } from './RichTextBox'
import { StyleLibraryModal } from './StyleLibraryModal'
import { getStyleOption } from '../../../../constants/styles'
import { useProjectId } from '../../../../contexts/ProjectIdContext'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ConfigNodeType = 'character' | 'location' | 'object' | 'imageGenerator'

export interface ConfigTabContentProps {
  nodeId: string
  nodeType: ConfigNodeType
  // Prompt
  prompt: string
  onPromptChange: (value: string) => void
  // References
  references: SelectedReference[]
  onReferencesChange: (refs: SelectedReference[]) => void
  // Style
  currentStyleId?: string
  onStyleChange: (styleId: string) => void
  // Node-type-specific config (ImageGenerator)
  aspectRatio?: string
  quality?: string
  onAspectRatioChange?: (ratio: string) => void
  onQualityChange?: (quality: string) => void
  // Node-type-specific config (LocationDesigner)
  timeOfDay?: string
  weather?: string
  onTimeOfDayChange?: (time: string) => void
  onWeatherChange?: (weather: string) => void
  // Execution
  isRunning: boolean
  onGenerate: (retryCount: number) => void
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PLACEHOLDER_TEXT: Record<ConfigNodeType, string> = {
  character: 'Describe your character...',
  location: 'Describe this location...',
  object: 'Describe this object...',
  imageGenerator: 'Describe the image to generate...',
}

/** Default sparks per image when pricing is unavailable */
const BASE_SPARKS = 3

// ImageGenerator flyout options
export const QUALITY_OPTIONS = ['512P', '1K', '2K', '4K'] as const
export const ASPECT_RATIO_GRID = [
  'Auto', '16:9', '9:16', '3:4', '4:3', '1:1',
] as const

// LocationDesigner flyout options
export const TIME_OF_DAY_OPTIONS = ['Dawn', 'Day', 'Dusk', 'Night'] as const
export const WEATHER_OPTIONS = ['Clear', 'Cloudy', 'Rainy', 'Foggy', 'Snowy', 'Stormy'] as const

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ConfigTabContent = memo(function ConfigTabContent({
  nodeId,
  nodeType,
  prompt,
  onPromptChange,
  references,
  onReferencesChange,
  currentStyleId,
  onStyleChange,
  aspectRatio = '16:9',
  quality = '1K',
  onAspectRatioChange,
  onQualityChange,
  timeOfDay = 'Day',
  weather = 'Clear',
  onTimeOfDayChange,
  onWeatherChange,
  isRunning,
  onGenerate,
}: ConfigTabContentProps) {
  const projectId = useProjectId()
  // ── Local state ──────────────────────────────────────────────────────────
  const [retryCount, setRetryCount] = useState(1)
  const [isRefSelectorOpen, setIsRefSelectorOpen] = useState(false)
  const [isStyleModalOpen, setIsStyleModalOpen] = useState(false)
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false)
  const [isRetryPopoverOpen, setIsRetryPopoverOpen] = useState(false)

  const flyoutRef = useRef<HTMLDivElement>(null)
  const pillRef = useRef<HTMLButtonElement>(null)
  const retryPillRef = useRef<HTMLButtonElement>(null)
  const retryPopoverRef = useRef<HTMLDivElement>(null)
  const { getNodes } = useReactFlow()

  // ── Computed values ──────────────────────────────────────────────────────
  const sparksCount = retryCount * BASE_SPARKS
  const hasConfigPill = nodeType === 'imageGenerator' || nodeType === 'location'

  const configPillLabel =
    nodeType === 'imageGenerator'
      ? `${aspectRatio} · ${quality}`
      : nodeType === 'location'
        ? `${timeOfDay} · ${weather}`
        : ''

  // ── Click-outside for flyout ─────────────────────────────────────────────
  useEffect(() => {
    if (!isFlyoutOpen) return

    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement
      if (
        flyoutRef.current &&
        !flyoutRef.current.contains(target) &&
        pillRef.current &&
        !pillRef.current.contains(target)
      ) {
        setIsFlyoutOpen(false)
      }
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        setIsFlyoutOpen(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isFlyoutOpen])

  // ── Click-outside for retry popover ───────────────────────────────────────
  useEffect(() => {
    if (!isRetryPopoverOpen) return

    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement
      if (
        retryPopoverRef.current &&
        !retryPopoverRef.current.contains(target) &&
        retryPillRef.current &&
        !retryPillRef.current.contains(target)
      ) {
        setIsRetryPopoverOpen(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
    }
  }, [isRetryPopoverOpen])

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleGenerate = useCallback(() => {
    onGenerate(retryCount)
  }, [onGenerate, retryCount])

  const handleRefSave = useCallback(
    (selected: SelectedReference[]) => {
      onReferencesChange(selected)
      setIsRefSelectorOpen(false)
    },
    [onReferencesChange]
  )

  const handleStyleApply = useCallback(
    (styleId: string) => {
      onStyleChange(styleId)
      setIsStyleModalOpen(false)
    },
    [onStyleChange]
  )

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div
      className="flex flex-col h-full p-3 gap-2"
      data-testid="config-tab-content"
    >
      {/* Row 1: References */}
      <div>
        <label className="text-[10px] uppercase tracking-wider text-text-muted block mb-1">
          References
        </label>
        <div className="flex items-center gap-2 min-h-[36px]" data-testid="reference-row">
        {/* Reference thumbnails */}
        {references.length > 0 && (
          <div className="flex gap-1 overflow-x-auto" data-testid="reference-thumbnails">
            {references.map((ref) => {
              const thumbUrl = getThumbnailUrl(projectId, ref.assetPath, 80)
              return (
                <div
                  key={ref.nodeId}
                  className="w-10 h-10 rounded-md overflow-hidden flex-shrink-0 border border-border-subtle cursor-pointer"
                  onClick={() => setIsRefSelectorOpen(true)}
                >
                  {thumbUrl ? (
                    <img
                      src={thumbUrl}
                      alt={ref.label}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-surface-2" />
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* [+] button */}
        <button
          onClick={() => setIsRefSelectorOpen(true)}
          className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-md border border-dashed border-border-subtle text-text-muted hover:text-foreground hover:border-primary/40 transition-colors"
          title="Add reference"
          data-testid="add-reference-btn"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
        </button>
        </div>
      </div>

      {/* Row 2: Prompt */}
      <div>
        <label className="text-[10px] uppercase tracking-wider text-text-muted block mb-1">
          Prompt
        </label>
        <RichTextBox
        containerClassName="flex-1 min-h-[60px] max-h-[160px] overflow-hidden w-full bg-surface-2 border border-border-subtle rounded-lg px-3 py-2 transition-colors focus-within:border-primary/50 nodrag nowheel"
        containerTestId="config-prompt-textarea"
        canvasNodes={getNodes()}
        placeholder={PLACEHOLDER_TEXT[nodeType]}
        initialContent={prompt}
        onContentChange={(state: RichTextBoxState) => onPromptChange(state.plainText)}
        onSubmit={() => {
          if (!isRunning) handleGenerate()
        }}
        minHeight={40}
        maxHeight={120}
        maxLength={1000}
        />
      </div>

      {/* Row 3: Control Bar */}
      <div className="flex items-center gap-2" data-testid="control-bar">
        {/* Config Pill (conditional) */}
        {hasConfigPill && (
          <div className="relative">
            <button
              ref={pillRef}
              onClick={() => setIsFlyoutOpen(!isFlyoutOpen)}
              className="px-2.5 py-1 text-xs font-medium rounded-full bg-surface-2 text-text-muted hover:text-foreground border border-border-subtle hover:border-primary/40 transition-colors whitespace-nowrap"
              data-testid="config-pill"
            >
              {configPillLabel}
            </button>

            {/* Config Flyout Popover */}
            {isFlyoutOpen && (
              <div
                ref={flyoutRef}
                className="absolute bottom-full left-0 mb-2 bg-surface-1 border border-border rounded-xl shadow-2xl p-3 z-50 min-w-[260px]"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                data-testid="config-flyout"
              >
                {nodeType === 'imageGenerator' && (
                  <ImageGeneratorFlyout
                    quality={quality}
                    aspectRatio={aspectRatio}
                    onQualityChange={(q) => onQualityChange?.(q)}
                    onAspectRatioChange={(r) => onAspectRatioChange?.(r)}
                  />
                )}
                {nodeType === 'location' && (
                  <LocationFlyout
                    timeOfDay={timeOfDay}
                    weather={weather}
                    onTimeOfDayChange={(t) => onTimeOfDayChange?.(t)}
                    onWeatherChange={(w) => onWeatherChange?.(w)}
                  />
                )}
              </div>
            )}
          </div>
        )}

        {/* Style Button */}
        <button
          onClick={() => setIsStyleModalOpen(true)}
          className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-surface-2 text-text-muted hover:text-foreground border border-border-subtle hover:border-primary/40 transition-colors"
          data-testid="style-button"
        >
          {/* Paintbrush icon */}
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42" />
          </svg>
          {getStyleOption(currentStyleId ?? '')?.label ?? 'Style'}
        </button>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Retry Count — Collapsed Pill with Popover */}
        <div className="relative" data-testid="retry-selector">
          <button
            ref={retryPillRef}
            onClick={() => setIsRetryPopoverOpen(!isRetryPopoverOpen)}
            className="px-2.5 py-1 text-xs font-medium rounded-full bg-surface-2 text-text-muted hover:text-foreground border border-border-subtle hover:border-primary/40 transition-colors whitespace-nowrap"
            data-testid="retry-pill"
          >
            {retryCount}x
          </button>

          {isRetryPopoverOpen && (
            <div
              ref={retryPopoverRef}
              className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-surface-1 border border-border rounded-lg shadow-2xl overflow-hidden z-50 min-w-[48px]"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              data-testid="retry-popover"
            >
              {([4, 3, 2, 1] as const).map((n) => (
                <button
                  key={n}
                  onClick={() => {
                    setRetryCount(n)
                    setIsRetryPopoverOpen(false)
                  }}
                  className={`w-full px-3 py-1.5 text-xs font-medium text-center transition-colors ${
                    retryCount === n
                      ? 'text-primary-foreground'
                      : 'text-text-muted hover:text-foreground hover:bg-surface-2'
                  }`}
                  style={
                    retryCount === n
                      ? { backgroundColor: 'var(--primary)' }
                      : undefined
                  }
                  data-testid={`retry-${n}x`}
                >
                  {n}x
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Sparks Count */}
        <span className="flex items-center gap-1 text-xs text-text-muted tabular-nums" data-testid="sparks-count">
          <svg className="w-3.5 h-3.5 text-yellow-400 shrink-0" viewBox="0 0 24 24" fill="currentColor">
            <path d="M13 3L4 14h7l-2 7 9-11h-7l2-7z" />
          </svg>
          {sparksCount}
        </span>

        {/* Generate Button */}
        <button
          onClick={handleGenerate}
          disabled={isRunning}
          className="w-8 h-8 flex items-center justify-center rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ backgroundColor: 'var(--primary)' }}
          title="Generate"
          data-testid="generate-button"
        >
          {/* Arrow-up icon */}
          <svg className="w-4 h-4 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      </div>

      {/* Reference Selector Modal */}
      <ReferenceSelector
        isOpen={isRefSelectorOpen}
        onClose={() => setIsRefSelectorOpen(false)}
        onSave={handleRefSave}
        excludeNodeId={nodeId}
        canvasNodes={getNodes()}
        currentReferences={references}
        maxReferences={4}
      />

      {/* Style Library Modal */}
      <StyleLibraryModal
        isOpen={isStyleModalOpen}
        onClose={() => setIsStyleModalOpen(false)}
        onApply={handleStyleApply}
        currentStyleId={currentStyleId ?? ''}
      />
    </div>
  )
})

ConfigTabContent.displayName = 'ConfigTabContent'

// ---------------------------------------------------------------------------
// ImageGenerator Flyout
// ---------------------------------------------------------------------------

interface ImageGeneratorFlyoutProps {
  quality: string
  aspectRatio: string
  onQualityChange: (quality: string) => void
  onAspectRatioChange: (ratio: string) => void
}

const ImageGeneratorFlyout = memo(function ImageGeneratorFlyout({
  quality,
  aspectRatio,
  onQualityChange,
  onAspectRatioChange,
}: ImageGeneratorFlyoutProps) {
  return (
    <div className="flex flex-col gap-3" data-testid="image-generator-flyout">
      {/* Quality */}
      <div>
        <label className="text-xxs uppercase tracking-wider text-text-muted block mb-1.5">
          Quality
        </label>
        <div className="flex gap-1">
          {QUALITY_OPTIONS.map((opt) => (
            <button
              key={opt}
              onClick={() => onQualityChange(opt)}
              className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${
                quality === opt
                  ? 'bg-primary/20 text-primary border border-primary/40'
                  : 'bg-surface-2 text-text-muted border border-border-subtle hover:text-foreground'
              }`}
              data-testid={`quality-${opt}`}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      {/* Aspect Ratio */}
      <div>
        <label className="text-xxs uppercase tracking-wider text-text-muted block mb-1.5">
          Aspect Ratio
        </label>
        <div className="grid grid-cols-3 gap-1" data-testid="aspect-ratio-grid">
          {ASPECT_RATIO_GRID.map((opt) => (
            <button
              key={opt}
              onClick={() => onAspectRatioChange(opt)}
              className={`py-1.5 rounded-md text-xs font-medium transition-colors ${
                aspectRatio === opt
                  ? 'bg-primary/20 text-primary border border-primary/40'
                  : 'bg-surface-2 text-text-muted border border-border-subtle hover:text-foreground'
              }`}
              data-testid={`ratio-${opt}`}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
})

ImageGeneratorFlyout.displayName = 'ImageGeneratorFlyout'

// ---------------------------------------------------------------------------
// LocationDesigner Flyout
// ---------------------------------------------------------------------------

interface LocationFlyoutProps {
  timeOfDay: string
  weather: string
  onTimeOfDayChange: (time: string) => void
  onWeatherChange: (weather: string) => void
}

const LocationFlyout = memo(function LocationFlyout({
  timeOfDay,
  weather,
  onTimeOfDayChange,
  onWeatherChange,
}: LocationFlyoutProps) {
  return (
    <div className="flex flex-col gap-3" data-testid="location-flyout">
      {/* Time of Day */}
      <div>
        <label className="text-xxs uppercase tracking-wider text-text-muted block mb-1.5">
          Time of Day
        </label>
        <div className="flex gap-1">
          {TIME_OF_DAY_OPTIONS.map((opt) => (
            <button
              key={opt}
              onClick={() => onTimeOfDayChange(opt)}
              className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${
                timeOfDay === opt
                  ? 'bg-primary/20 text-primary border border-primary/40'
                  : 'bg-surface-2 text-text-muted border border-border-subtle hover:text-foreground'
              }`}
              data-testid={`time-${opt}`}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      {/* Weather */}
      <div>
        <label className="text-xxs uppercase tracking-wider text-text-muted block mb-1.5">
          Weather
        </label>
        <div className="grid grid-cols-3 gap-1">
          {WEATHER_OPTIONS.map((opt) => (
            <button
              key={opt}
              onClick={() => onWeatherChange(opt)}
              className={`py-1.5 rounded-md text-xs font-medium transition-colors ${
                weather === opt
                  ? 'bg-primary/20 text-primary border border-primary/40'
                  : 'bg-surface-2 text-text-muted border border-border-subtle hover:text-foreground'
              }`}
              data-testid={`weather-${opt}`}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
})

LocationFlyout.displayName = 'LocationFlyout'

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export { ConfigTabContent }
